import IOSSwitch from './IosSwitch';
import type { IntegrationKey, IntegrationOption } from './types';

type IntegrationsSectionProps = {
  data: {
    settings: Record<IntegrationKey, boolean>;
    options: IntegrationOption[];
    isGoogleAuthLoading: boolean;
    googleAuthErrorMessage: string;
    isMicrosoftAuthLoading?: boolean;
    microsoftAuthErrorMessage?: string;
    isZoomAuthLoading?: boolean;
    zoomAuthErrorMessage?: string;
  };
  actions: {
    onToggle: (key: IntegrationKey) => void;
    onManage: (key: IntegrationKey) => void;
  };
};

const IntegrationsSection = ({ data, actions }: IntegrationsSectionProps) => {
  const {
    settings,
    options,
    isGoogleAuthLoading,
    googleAuthErrorMessage,
    isMicrosoftAuthLoading = false,
    microsoftAuthErrorMessage = '',
    isZoomAuthLoading = false,
    zoomAuthErrorMessage = '',
  } = data;
  const { onToggle, onManage } = actions;

  return (
  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
    {options.map((option) => {
      const isGoogle = option.key === 'googleCalendar';
      const isMicrosoft = option.key === 'outlookCalendar';
      const isZoom = option.key === 'zoomCalendar';
      const isAuthLoading =
        (isGoogle && isGoogleAuthLoading) ||
        (isMicrosoft && isMicrosoftAuthLoading) ||
        (isZoom && isZoomAuthLoading);
      const errorMessage = isGoogle
        ? googleAuthErrorMessage
        : isMicrosoft
          ? microsoftAuthErrorMessage
          : isZoom
            ? zoomAuthErrorMessage
            : '';
      const actionLabel = 'Manage';
      return (
        <div
          key={option.key}
          className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-[12px] border border-slate-200 ${option.iconBg}`}
            >
              <span className={`text-sm font-semibold ${option.iconText}`}>
                {option.icon}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">
                {option.label}
              </p>
              <p className="mt-1 text-xs text-slate-500">{option.description}</p>
            </div>
          </div>
          <div className="mt-4 border-b border-slate-100" />
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              className="flex items-center gap-2 text-xs font-semibold text-slate-500 transition hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
              onClick={() => onManage(option.key)}
              disabled={isAuthLoading}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82v0a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 9 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              {isAuthLoading ? 'Connecting...' : actionLabel}
            </button>
            <IOSSwitch
              checked={settings[option.key]}
              onChange={() => onToggle(option.key)}
              disabled={isAuthLoading}
              inputProps={{ 'aria-label': `Toggle ${option.label}` }}
            />
          </div>
          {errorMessage ? (
            <p className="mt-3 text-xs font-semibold text-rose-500">
              {errorMessage}
            </p>
          ) : null}
        </div>
      );
    })}
  </div>
  );
};

export default IntegrationsSection;
